/*
 * Lemonade Training Oy, copy rights, all right reserved.
 * Display records derived from the athlete's MovementState document, one per
 * movement. The registry owns the unit, name and area, so a movement missing
 * from it is never shown or prescribed from.
 */
import {
  calendarDateKey,
  coerceStoredDate,
  profileEntry,
  type MovementRegistryEntry,
  type MovementState,
  type MovementStateEntry,
  type OneRepMax,
} from "@lemonadetraining/contracts";
import type { MovementStateStatus } from "../hooks/useMovementState";
import { usableZone } from "./gymTime";

export type StateRecord = OneRepMax & { movementRegistryID: string; unlocked?: boolean };

type StateMap = ReadonlyMap<string, MovementState>;

/** Error wins over loading, so a failed read never shows as a forever skeleton. */
export function combineStatus(
  registry: "loading" | "ready" | "error",
  state: MovementStateStatus
): "loading" | "ready" | "error" {
  if (registry === "error" || state === "error") return "error";
  if (registry === "loading" || state === "idle" || state === "loading") return "loading";
  return "ready";
}

function currentEntry(state: MovementState): MovementStateEntry | null {
  return state.current ?? state.latest;
}

function toRecord(
  movementID: string,
  entry: MovementStateEntry,
  registryEntry: MovementRegistryEntry
): StateRecord {
  const when = coerceStoredDate(entry.date);
  const record: StateRecord = {
    id: entry.recordId,
    movementRegistryID: movementID,
    value: entry.value,
    weight: entry.value,
    kind: entry.kind,
    name: registryEntry.name,
    type: "",
    measureType: registryEntry.measureType,
    radarAxis: registryEntry.radarAxis,
    performedAt: when,
    updatedAt: when,
  };
  if (typeof entry.unlocked === "boolean") record.unlocked = entry.unlocked;
  return record;
}

function recordsFrom(
  movements: StateMap,
  registry: readonly MovementRegistryEntry[],
  pick: (state: MovementState, registryEntry: MovementRegistryEntry) => MovementStateEntry | null
): StateRecord[] {
  const byID = new Map(registry.map((m) => [m.id, m]));
  const records: StateRecord[] = [];
  for (const [movementID, state] of movements) {
    const registryEntry = byID.get(movementID);
    if (!registryEntry) continue;
    const entry = pick(state, registryEntry);
    if (entry) records.push(toRecord(movementID, entry, registryEntry));
  }
  return records;
}

/** The athlete's current value per movement; a working-set estimate only when nothing else exists. */
export function currentRecordsFromState(
  movements: StateMap,
  registry: readonly MovementRegistryEntry[]
): StateRecord[] {
  return recordsFrom(movements, registry, currentEntry);
}

/** Tested values only: what a percentage load may be prescribed from. */
export function testedRecordsFromState(
  movements: StateMap,
  registry: readonly MovementRegistryEntry[]
): StateRecord[] {
  return recordsFrom(movements, registry, (s) => s.current);
}

/** The entry the fitness profile shows per movement (contracts `profileEntry`); with the flag on, what the radar scores. */
export function profileRecordsByMovement(
  movements: StateMap,
  registry: readonly MovementRegistryEntry[],
  stateReadLive: boolean
): Map<string, StateRecord> {
  return new Map(
    recordsFrom(movements, registry, (s, m) => profileEntry(s, m.scoringType, stateReadLive)).map(
      (r) => [r.movementRegistryID, r]
    )
  );
}

/** The entry the movement page headlines and cascades from: with movementStateRead on, a tested one only. */
export function headlineEntry(
  state: MovementState | undefined,
  stateReadLive: boolean
): MovementStateEntry | null {
  const entry = state ? (stateReadLive ? state.current : currentEntry(state)) : null;
  return entry && entry.value > 0 ? entry : null;
}

/** Each movement's tested value: what a related movement shows instead of an estimate. */
export function testedValuesFromState(movements: StateMap): Map<string, number> {
  const tested = new Map<string, number>();
  for (const [movementID, state] of movements) {
    const value = state.current?.value;
    if (typeof value === "number" && value > 0) tested.set(movementID, value);
  }
  return tested;
}

/** The newest record of the movement, when it was done on `now`'s calendar day in `zone` (null: this browser's). */
export function todayRecordIdFromState(
  state: MovementState | undefined,
  zone: string | null,
  now: Date = new Date()
): string | null {
  const latest = state?.latest;
  const done = latest ? coerceStoredDate(latest.date) : null;
  if (!latest || !done) return null;
  const inZone = usableZone(zone);
  const today = calendarDateKey(now, inZone);
  return today !== null && calendarDateKey(done, inZone) === today ? latest.recordId : null;
}
